// SEO Component for dynamic meta tags
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;
const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'SAE Agro-Equipement';

// Default meta data
const DEFAULT_META = {
  title: 'SAE Agro-Equipement - Équipements Agricoles & Industriels en Algérie',
  description: 'SAE Agro-Equipement, distributeur officiel de pièces et équipements agricoles et industriels en Algérie depuis 1998. SKF, MAHLE, RIMA, NTN-SNR et plus.',
  image: '/logo.png',
  type: 'website'
};

// Page-specific meta data
const PAGE_META = {
  '/': {
    title: 'SAE Agro-Equipement - Équipements Agricoles & Industriels en Algérie',
    description: 'Distributeur officiel de pièces et équipements agricoles et industriels en Algérie depuis 1998. Marques: SKF, MAHLE, RIMA, NTN-SNR.',
    keywords: 'équipement agricole, pièces détachées, tracteur, moissonneuse, Algérie, SAE, SKF, MAHLE, RIMA'
  },
  '/equipment': {
    title: 'Équipements & Pièces Détachées - SAE Agro-Equipement',
    description: 'Découvrez notre gamme complète d\'équipements agricoles et industriels. Roulements, filtres, courroies, pièces moteur et plus.',
    keywords: 'roulements, filtres, courroies, pièces moteur, équipement agricole, Algérie'
  },
  '/services': {
    title: 'Nos Services - SAE Agro-Equipement',
    description: 'Services de vente, maintenance, installation et formation pour vos équipements agricoles et industriels en Algérie.',
    keywords: 'service après-vente, maintenance, installation, formation, équipement agricole'
  },
  '/partners': {
    title: 'Nos Partenaires - SAE Agro-Equipement',
    description: 'Découvrez nos partenaires de renommée mondiale: SKF, MAHLE, RIMA, NTN-SNR, et bien d\'autres marques leaders.',
    keywords: 'SKF, MAHLE, RIMA, NTN-SNR, partenaires, distributeur officiel, Algérie'
  },
  '/references': {
    title: 'Nos Références - SAE Agro-Equipement',
    description: 'Nos clients nous font confiance depuis plus de 25 ans. Découvrez nos références et témoignages clients.',
    keywords: 'références clients, témoignages, avis clients, équipement agricole Algérie'
  },
  '/contact': {
    title: 'Contactez-nous - SAE Agro-Equipement',
    description: 'Contactez SAE Agro-Equipement à Oran, Algérie. Demande de devis, informations produits, service client.',
    keywords: 'contact, devis, Oran, Algérie, équipement agricole, service client'
  },
  '/become-partner': {
    title: 'Devenir Partenaire - SAE Agro-Equipement',
    description: 'Rejoignez le réseau de partenaires SAE Agro-Equipement. Opportunités de partenariat en Algérie et au Maghreb.',
    keywords: 'partenariat, distributeur, revendeur, Algérie, Maghreb, équipement agricole'
  }
};

const SEO = ({ 
  title, 
  description, 
  image, 
  type = 'website',
  article = null,
  noindex = false 
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Get page-specific meta or use defaults
  const pageMeta = PAGE_META[currentPath] || {};
  
  const finalTitle = title || pageMeta.title || DEFAULT_META.title;
  const finalDescription = description || pageMeta.description || DEFAULT_META.description;
  const finalImage = image || pageMeta.image || DEFAULT_META.image;
  const finalKeywords = pageMeta.keywords || '';
  const canonicalUrl = `${SITE_URL}${currentPath}`;
  
  useEffect(() => {
    // Update document title
    document.title = finalTitle;
    
    // Helper function to update or create meta tag
    const updateMeta = (name, content, isProperty = false) => {
      if (!content) return;
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Helper to update or create link tag
    const updateLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    
    // Basic meta tags
    updateMeta('description', finalDescription);
    updateMeta('keywords', finalKeywords);
    
    // Robots
    if (noindex) {
      updateMeta('robots', 'noindex, nofollow');
    } else {
      updateMeta('robots', 'index, follow');
    }
    
    // Canonical URL
    updateLink('canonical', canonicalUrl);
    
    // Open Graph tags
    updateMeta('og:title', finalTitle, true);
    updateMeta('og:description', finalDescription, true);
    updateMeta('og:type', type, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:site_name', SITE_NAME, true);
    updateMeta('og:locale', 'fr_FR', true);
    if (finalImage) {
      const imageUrl = finalImage.startsWith('http') ? finalImage : `${SITE_URL}${finalImage}`;
      updateMeta('og:image', imageUrl, true);
    }
    
    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', finalTitle);
    updateMeta('twitter:description', finalDescription);
    if (finalImage) {
      const imageUrl = finalImage.startsWith('http') ? finalImage : `${SITE_URL}${finalImage}`;
      updateMeta('twitter:image', imageUrl);
    }
    
    // Article specific tags
    if (article && type === 'article') {
      updateMeta('article:published_time', article.publishedTime, true);
      updateMeta('article:modified_time', article.modifiedTime, true);
      updateMeta('article:author', article.author, true);
    }
    
    // Structured data (JSON-LD)
    let scriptTag = document.querySelector('script[data-seo="structured-data"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-seo', 'structured-data');
      document.head.appendChild(scriptTag);
    }
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": SITE_NAME,
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo.png`,
      "description": DEFAULT_META.description,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Oran",
        "addressCountry": "DZ"
      },
      "foundingDate": "1998",
      "sameAs": []
    };
    
    scriptTag.textContent = JSON.stringify(structuredData);
    
  }, [finalTitle, finalDescription, finalImage, finalKeywords, canonicalUrl, type, article, noindex]);
  
  return null; // This component doesn't render anything
};

export default SEO;
